import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getSafeDistanceMiles,
  normalizeCoordinates,
  normalizeProfileLocation,
} from "../../utils/locationUtils";
import CreateCommunityPost from "../Community/CreateCommunityPost";
import CommunityPostCard from "../Community/CommunityPostCard";
import { AuthActionGate, useAuthActionGate } from "../Common/AuthActionGate";
import { trackUsageEvent } from "../Analytics/CampaignTrafficTracker";

const RADIUS_OPTIONS = [
  { value: "25", label: "25 miles" },
  { value: "50", label: "50 miles" },
  { value: "100", label: "100 miles" },
  { value: "250", label: "250 miles" },
  { value: "anywhere", label: "Anywhere" },
];

const LAND_USE_TYPES = [
  { value: "all", label: "All land types" },
  { value: "horse_grazing", label: "Horse grazing pasture" },
  { value: "livestock_grazing", label: "Livestock grazing" },
  { value: "fenced_dog_run", label: "Fenced dog run" },
  { value: "garden_plot", label: "Garden plot" },
  { value: "hay_field_access", label: "Hay field access" },
  { value: "equipment_parking", label: "Equipment parking" },
  { value: "temporary_holding", label: "Temporary holding area" },
  { value: "other", label: "Other land use" },
];

const getUserCoordinates = (userLocation, userProfile) => {
  return normalizeCoordinates(userLocation) || normalizeCoordinates(normalizeProfileLocation(userProfile));
};

const getPostDistance = (post, userCoordinates) => {
  if (!userCoordinates) return null;
  return getSafeDistanceMiles(userCoordinates, post);
};

const normalize = (value) => String(value || "").toLowerCase().trim();

const LandSharePage = ({ currentUser, userProfile, userLocation }) => {
  const navigate = useNavigate();
  const { requireSignIn, gateProps: authGateProps } = useAuthActionGate(currentUser);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [offeringFilter, setOfferingFilter] = useState("all");
  const [landTypeFilter, setLandTypeFilter] = useState("all");
  const [radius, setRadius] = useState("100");
  const [search, setSearch] = useState("");

  const requireLandShareAction = (message) => {
    if (!currentUser?.uid) {
      trackUsageEvent("land_share_contact_or_post_intent", { feature: "land_share", action: "intent" });
      trackUsageEvent("auth_gate_open", { feature: "land_share", action: "land_share_contact_or_post_intent" });
    }
    return requireSignIn(message);
  };

  const userCoordinates = useMemo(
    () => getUserCoordinates(userLocation, userProfile),
    [userLocation, userProfile]
  );

  useEffect(() => {
    const postsQuery = query(
      collection(db, "communityPosts"),
      where("type", "==", "land_use"),
      where("status", "==", "active"),
      limit(75)
    );

    const unsubscribe = onSnapshot(
      postsQuery,
      (snapshot) => {
        const nextPosts = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((post) => post.type === "land_use" || post.category === "land_use")
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
            return bTime - aTime;
          });

        setPosts(nextPosts);
        setLoading(false);
      },
      (error) => {
        console.error("Land Share posts listener failed:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredPosts = useMemo(() => {
    const searchTerm = normalize(search);
    const selectedRadius = radius === "anywhere" ? null : Number(radius);

    return posts
      .filter((post) => post.isArchived !== true && post.isHidden !== true)
      .filter((post) => (post.status || "active") === "active")
      .filter((post) =>
        offeringFilter === "all" ? true : post.offeringOrSeeking === offeringFilter
      )
      .filter((post) => (landTypeFilter === "all" ? true : post.landUseType === landTypeFilter))
      .filter((post) => {
        if (!searchTerm) return true;
        return normalize(
          [
            post.title,
            post.text,
            post.description,
            post.locationText,
            post.city,
            post.state,
            post.landUseType,
            post.offeringOrSeeking,
            post.animalAllowedNotes,
            "pasture grazing fenced dog run garden plot field access hay equipment parking temporary holding",
          ].join(" ")
        ).includes(searchTerm);
      })
      .map((post) => ({
        ...post,
        distanceMiles: getPostDistance(post, userCoordinates),
      }))
      .filter((post) => {
        if (radius === "anywhere") return true;
        if (!userCoordinates) return true;
        if (typeof post.distanceMiles === "number") return post.distanceMiles <= selectedRadius;
        return Boolean(searchTerm || offeringFilter !== "all" || landTypeFilter !== "all");
      });
  }, [landTypeFilter, offeringFilter, posts, radius, search, userCoordinates]);

  return (
    <main className="land-share-page page-shell-wide">
      <section className="land-share-hero">
        <div className="land-share-hero-copy">
          <span className="land-share-badge">Local land access</span>
          <h1>Land Share</h1>
          <p>
            Offer or find pasture, grazing space, fenced areas, dog runs, garden plots,
            field access, hay field access, equipment parking, and temporary holding areas.
          </p>
          <div className="land-share-hero-actions">
            <a className="btn-primary" href="#land-share-browse">
              Browse Land Share
            </a>
            <button
              type="button"
              className="btn-soft"
              onClick={() => {
                if (requireLandShareAction("Create a free account or sign in to post Land Share.")) {
                  setShowPostForm(true);
                }
              }}
            >
              Post Land Share
            </button>
          </div>
        </div>

        <div className="land-share-hero-panel" aria-hidden="true">
          <span>🧭</span>
          <strong>Map the usable area</strong>
          <p>Draw available pasture, garden plots, fenced areas, or field access on the map.</p>
        </div>
      </section>

      <section className="land-share-post-panel">
        <div>
          <p className="eyebrow">Post Land Share</p>
          <h2>Show what is available</h2>
          <p>
            Draw the pasture, garden plot, fenced area, or field access area so nearby
            people can see what is available. You can also share a text-only post.
          </p>
        </div>
        <div className="land-share-post-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (requireLandShareAction("Create a free account or sign in to map a Land Share area.")) {
                navigate("/map", { state: { postType: "land_use" } });
              }
            }}
          >
            Draw land area on map
          </button>
          <button
            type="button"
            className="btn-soft"
            onClick={() => {
              if (requireLandShareAction("Create a free account or sign in to post Land Share.")) {
                setShowPostForm((current) => !current);
              }
            }}
          >
            {showPostForm ? "Hide text form" : "Post text listing"}
          </button>
        </div>
      </section>

      {showPostForm && (
        <section className="land-share-create-wrap">
          <CreateCommunityPost
            currentUser={currentUser}
            userProfile={userProfile}
            userLocation={userLocation}
            initialType="land_use"
            onCreated={() => setShowPostForm(false)}
          />
        </section>
      )}

      <section className="land-share-browse-section" id="land-share-browse">
        <div className="land-share-section-heading">
          <div>
            <p className="eyebrow">Browse Land Share</p>
            <h2>Nearby land access posts</h2>
          </div>
          <p>Newest active Land Use posts from the community board.</p>
        </div>

        <div className="land-share-filters">
          <label className="form-label">
            Offering / Seeking
            <select value={offeringFilter} onChange={(event) => setOfferingFilter(event.target.value)}>
              <option value="all">All</option>
              <option value="offering">Offering</option>
              <option value="seeking">Seeking</option>
            </select>
          </label>

          <label className="form-label">
            Land use type
            <select value={landTypeFilter} onChange={(event) => setLandTypeFilter(event.target.value)}>
              {LAND_USE_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-label">
            Distance
            <select value={radius} onChange={(event) => setRadius(event.target.value)}>
              {RADIUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-label">
            Search
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Pasture, garden, fenced, hay..."
            />
          </label>
        </div>

        {loading ? (
          <div className="land-share-empty-state">Loading Land Share posts...</div>
        ) : filteredPosts.length === 0 ? (
          <div className="land-share-empty-state">
            <strong>No land share posts yet.</strong>
            <p>
              Offer or find pasture, grazing, garden plots, fenced areas, or field access nearby.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (requireLandShareAction("Create a free account or sign in to post Land Share.")) {
                  setShowPostForm(true);
                }
              }}
            >
              Post Land Share
            </button>
          </div>
        ) : (
          <section className="land-share-post-list">
            {filteredPosts.map((post) => (
              <CommunityPostCard
                key={post.id}
                post={post}
                currentUser={currentUser}
                userProfile={userProfile}
                distanceMiles={post.distanceMiles}
              />
            ))}
          </section>
        )}
      </section>

      <section className="land-share-map-note">
        <div>
          <strong>Need the exact field boundary?</strong>
          <p>Mapped Land Share posts can show polygons and entrance markers on the map.</p>
        </div>
        <button
          type="button"
          className="btn-soft"
          onClick={() => {
            if (requireLandShareAction("Create a free account or sign in to open the map.")) {
              navigate("/map");
            }
          }}
        >
          Open Map
        </button>
      </section>
      <AuthActionGate {...authGateProps} />
    </main>
  );
};

export default LandSharePage;
